import React from 'react';
import { Card, Typography, Row, Col, Space } from 'antd';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const { Title } = Typography;

interface PageData {
  pageName: string;
  pageType: string;
  visitCount: number;
  uniqueUsers: number;
  avgDuration: number;
}

interface PieDataItem {
  type: string;
  value: number;
  percentage: string;
  color?: string;
}

interface PopularPagesProps {
  data: PageData[];
  loading: boolean;
}

const PopularPages: React.FC<PopularPagesProps> = ({ data, loading }) => {
  // PAGE_TYPE映射 - 只统计2到8
  const PAGE_TYPE_MAPPING: { [key: string]: string } = {
    '計測': '計測',
    'ポリコン表': 'ポリコン表', 
    'DIPモード': 'DIPモード',
    '重ね合わせ': '重ね合わせ',
    'プロフィログラム': 'プロフィログラム',
    '評価': '評価',
    'PA analysis': 'PA analysis'
  };

  // 过滤并处理数据，只包含PAGE_TYPE 2-8
  const getFilteredPieData = (): PieDataItem[] => {
    // 先过滤只包含目标页面类型的数据
    const filteredData = data.filter(item => 
      Object.keys(PAGE_TYPE_MAPPING).includes(item.pageName)
    );

    // 合并相同页面的数据，避免重复
    const mergedData = filteredData.reduce((acc: PageData[], item) => {
      const existingItem = acc.find((existing: PageData) => existing.pageName === item.pageName);
      if (existingItem) {
        existingItem.visitCount += item.visitCount;
        existingItem.uniqueUsers += item.uniqueUsers;
      } else {
        acc.push({ ...item });
      }
      return acc;
    }, [] as PageData[]);

    const totalVisits = mergedData.reduce((sum: number, item: PageData) => sum + item.visitCount, 0);
    
    // 定义固定的颜色顺序
    const pageColorMap: { [key: string]: string } = {
      '計測': pieColors[0],
      'ポリコン表': pieColors[1],
      'DIPモード': pieColors[2],
      'プロフィログラム': pieColors[3],
      '重ね合わせ': pieColors[4],
      '評価': pieColors[5],
      'PA analysis': pieColors[6]
    };
    
    return mergedData
      .filter((item: PageData) => item.visitCount > 0) // 过滤掉访问次数为0的项目
      .map((item: PageData): any => ({
        type: item.pageName,
        value: item.visitCount,
        percentage: totalVisits > 0 ? ((item.visitCount / totalVisits) * 100).toFixed(1) : '0.0',
        color: pageColorMap[item.pageName] || '#999'
      }));
  };

  // 饼图颜色配置
  const pieColors = [
    '#1890ff', // 蓝色
    '#52c41a', // 绿色  
    '#faad14', // 金色
    '#f5222d', // 红色
    '#722ed1', // 紫色
    '#fa8c16', // 橙色
    '#13c2c2'  // 青色
  ];

  const pieData = getFilteredPieData();
  console.log('饼图数据检查:', pieData);

  // 使用数据中已定义的颜色
  const colorMapping: { [key: string]: string } = {};
  pieData.forEach((item: any) => {
    colorMapping[item.type] = item.color;
  });

  // 不再需要 pieConfig，因为我们使用 Recharts

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>ページ使用統計</Title>
          <span style={{ color: '#999', fontSize: '14px' }}>Page Usage Statistics</span>
        </Space>
      }
      style={{ height: '480px' }}
    >
      <div style={{ height: 'calc(100% - 60px)', padding: '16px' }}>
        {!loading && getFilteredPieData().length > 0 ? (
          <Row gutter={[16, 16]} style={{ height: '100%' }}>
            <Col xs={24} sm={12} style={{ height: '100%' }}>
              <div style={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center'
              }}>
                <ResponsiveContainer width="100%" height={360}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={130}
                      innerRadius={65}
                      dataKey="value"
                      label={false}
                    >
                      {pieData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={({ active, payload }: any) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                              padding: '8px 12px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{data.type}</div>
                              <div style={{ color: '#666' }}>{data.value}回 ({data.percentage}%)</div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col xs={24} sm={12} style={{ height: '100%' }}>
              <div style={{ padding: '8px 0', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Title level={5} style={{ marginBottom: '8px', fontSize: '14px' }}>統計サマリー</Title>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {(() => {
                    const sortedData = [...pieData].sort((a: PieDataItem, b: PieDataItem) => b.value - a.value);
                    
                    return sortedData.map((item: PieDataItem, sortedIndex: number) => {
                      return (
                        <div key={item.type} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 8px',
                          background: sortedIndex === 0 ? '#f6ffed' : '#fafafa',
                          borderRadius: '4px',
                          border: sortedIndex === 0 ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
                          marginBottom: '4px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{
                              width: '8px',
                              height: '8px',
                              backgroundColor: colorMapping[item.type],
                              borderRadius: '2px',
                              marginRight: '4px'
                            }}></div>
                            <span style={{ fontSize: '11px' }}>{item.type}</span>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                              {item.value}回
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>
                              {item.percentage}%
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </Col>
          </Row>
        ) : (
          <div style={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#999'
          }}>
            {loading ? '読み込み中...' : 'データがありません'}
          </div>
        )}
      </div>
    </Card>
  );
};

export default PopularPages;